import { matchPath } from "react-router-dom";
import { defaultTranslations } from "../../constants/defaults";
import * as ROUTES from "../../constants/routes";
import { getBackendUrl, mergeSettings } from "../../helpFunctions/general";

const decodeRouteParam = value => {
  if (!value) return value;

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const getDeploymentEnvironmentInfo = (deploymentEnvironment = import.meta.env.VITE_DEPLOYMENT_ENV) => {
  switch (deploymentEnvironment) {
    case "dev":
      return {
        title: "DEV",
        hasSpecialNavTone: true,
        label: "DEV",
        tooltip: "This is a development version of the app. It may have unfinished features and more bugs.",
      };
    case "prod":
      return {
        title: "PRODUCTION",
        hasSpecialNavTone: false,
        label: null,
        tooltip: "This is the production version of the app.",
      };
    default:
      return {
        title: deploymentEnvironment?.toUpperCase?.() || "UNKNOWN",
        hasSpecialNavTone: false,
        label: null,
        tooltip: "This is the current deployment environment.",
      };
  }
};

export const getFrontendTargetInfo = (frontendTarget = import.meta.env.VITE_FRONTEND_TARGET) => {
  switch (frontendTarget) {
    case "primary":
      return {
        title: "PRIMARY",
        hasSpecialNavTone: false,
        label: null,
        labelPlacement: null,
        tooltip: "This is the primary frontend target.",
      };
    case "alpha":
      return {
        title: "ALPHA",
        hasSpecialNavTone: true,
        label: "ALPHA",
        labelPlacement: "main",
        tooltip: "This is an alpha version of the app. It may have the newest features but also more bugs.",
      };
    case "beta":
      return {
        title: "BETA",
        hasSpecialNavTone: false,
        label: null,
        labelPlacement: null,
        tooltip: "This is the beta version of the app. It may have the newest features but also more bugs.",
      };
  }

  const title = frontendTarget?.toUpperCase?.() || "UNKNOWN";
  return {
    title,
    hasSpecialNavTone: true,
    label: title,
    labelPlacement: "sub",
    tooltip: `This is the ${frontendTarget || "unknown"} frontend target.`,
  };
};

const resolveScenarioIdFromPath = pathname => {
  const scenarioMatch =
    matchPath(`${ROUTES.SCENARIO}/:id`, pathname) ||
    matchPath(`${ROUTES.LEGACY_SCENARIO_VIEW}/:id`, pathname) ||
    matchPath(`${ROUTES.PLAY_PAGE}/:id`, pathname);
  const scenarioEditorMatch = matchPath(`${ROUTES.SCENARIO_EDITOR}/:id`, pathname);

  return {
    scenarioEditorId: decodeRouteParam(scenarioEditorMatch?.params?.id),
    scenarioId: decodeRouteParam(scenarioMatch?.params?.id),
  };
};

export const resolveCurrentBackendSettings = ({ pathname, state }) => {
  const baseSettings = state?.settings || {};
  const { scenarioEditorId, scenarioId } = resolveScenarioIdFromPath(pathname);

  if (scenarioEditorId) {
    const scenario = state?.scenarios?.find(item => item.id === scenarioEditorId);
    return mergeSettings([baseSettings, scenario?.options || {}]);
  }

  if (scenarioId) {
    const scenario = state?.scenarios?.find(item => item.id === scenarioId);
    return mergeSettings([baseSettings, scenario?.options || {}]);
  }

  return baseSettings;
};

export const resolveCurrentBackendChannel = ({ pathname, state }) => {
  return resolveCurrentBackendSettings({ pathname, state }).backendChannel;
};

export const getBackendChannelTitle = backendChannel => {
  if (!backendChannel || backendChannel === "default") return null;
  return defaultTranslations?.settings?.backendChannel?.[backendChannel]?.title || backendChannel;
};

export const getBackendChannelInfo = settings => {
  const backendChannel = settings?.backendChannel;
  const backendTranslations = defaultTranslations?.settings?.backendChannel;
  const title = backendTranslations?.[backendChannel || "default"]?.title || backendChannel || "default";
  const url = getBackendUrl({ settings, logResolution: false });

  return {
    title,
    url,
  };
};
